// frontend/src/components/Profile/ProfileProjectCard.js
import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, Avatar, Tooltip, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getHeaderGradient } from '../../utils/getImageColors';

// Функция для получения URL аватарки владельца
const getOwnerAvatarUrl = (project) => {
  if (!project?.owner?.avatar) return null;
  if (project.owner.avatar.startsWith('http')) return project.owner.avatar;
  return `https://fincollab.ru${project.owner.avatar}`;
};

// Трехслойная стопка
const CardStack = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 280,
  height: 240,
  margin: '0 auto',
  cursor: 'pointer',
  perspective: '1200px'
}));

const CardLayer = styled(Paper)(({ theme, index, isHovered, gradientColor }) => {
  const getTransform = () => {
    const baseOffset = index * 8;

    let translateY = baseOffset;
    let translateX = baseOffset;
    let rotate = 0;

    if (isHovered) {
      const raiseAmount = [10, 16, 22];
      translateY = baseOffset - raiseAmount[index];
      translateX = baseOffset + index * 3;
      rotate = index * 0.3;
    }

    return `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg)`;
  };

  const getBackgroundColor = () => {
    if (index === 2 && gradientColor) {
      return 'transparent';
    }
    if (index === 2) return '#ffffff';
    if (index === 1) return '#ffffff';
    return '#fafafa';
  };

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: theme.spacing(4),
    backgroundColor: getBackgroundColor(),
    ...(index === 2 && gradientColor && {
      background: gradientColor,
    }),
    boxShadow: index === 2
      ? '0 4px 20px rgba(0,0,0,0.08)'
      : '0 2px 8px rgba(0,0,0,0.04)',
    zIndex: index,
    transform: getTransform(),
    transition: 'transform 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
    transformStyle: 'preserve-3d',
    willChange: 'transform',
    transitionDelay: isHovered
      ? `${index * 120}ms`
      : `${(2 - index) * 150}ms`,
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: theme.spacing(4),
      border: index < 2 ? '1px solid rgba(0,0,0,0.03)' : 'none',
      pointerEvents: 'none',
    },
    ...(index < 2 && {
      borderRight: '1px solid rgba(0,0,0,0.05)',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
    }),
  };
});

const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 10,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(4),
  overflow: 'hidden',
  backgroundColor: 'transparent'
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 2, 2, 2),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left'
}));

const Divider = styled(Box)(({ theme }) => ({
  height: '1px',
  backgroundColor: 'rgba(255,255,255,0.3)',
  width: '100%',
  margin: theme.spacing(0.5, 0)
}));

const CardFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginTop: 'auto',
  flexShrink: 0,
  width: '100%',
  textAlign: 'left'
}));

const ProfileProjectCard = ({ project, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [headerGradient, setHeaderGradient] = useState(null);
  const [loadingGradient, setLoadingGradient] = useState(false);

  const ownerAvatarUrl = getOwnerAvatarUrl(project);

  useEffect(() => {
    if (ownerAvatarUrl) {
      setLoadingGradient(true);
      getHeaderGradient(ownerAvatarUrl)
        .then(gradient => {
          setHeaderGradient(gradient);
        })
        .catch(() => {
          setHeaderGradient(null);
        })
        .finally(() => {
          setLoadingGradient(false);
        });
    } else {
      setHeaderGradient(null);
    }
  }, [ownerAvatarUrl]);

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'completed': 'default',
      'draft': 'default',
      'archived': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Активный',
      'completed': 'Завершен',
      'draft': 'Черновик',
      'archived': 'Архив'
    };
    return labels[status] || status;
  };

  const hasGradient = headerGradient && !loadingGradient;

  return (
    <CardStack
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* Слой 0 - самый нижний */}
      <CardLayer index={0} isHovered={isHovered} elevation={0}>
        <Box sx={{ width: '100%', height: '100%', borderRadius: 4 }} />
      </CardLayer>

      {/* Слой 1 - средний */}
      <CardLayer index={1} isHovered={isHovered} elevation={0}>
        <Box sx={{ width: '100%', height: '100%', borderRadius: 4 }} />
      </CardLayer>

      {/* Слой 2 - верхний с контентом */}
      <CardLayer
        index={2}
        isHovered={isHovered}
        elevation={0}
        gradientColor={hasGradient ? headerGradient : null}
      >
        <ContentContainer>
          <CardContent>
            <Typography
              variant="subtitle1"
              fontWeight="600"
              sx={{
                mb: 1,
                fontSize: '1rem',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                textAlign: 'left',
                color: hasGradient ? 'white' : 'inherit'
              }}
            >
              {project.title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
                flex: 1,
                textAlign: 'left',
                color: hasGradient ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                fontWeight: hasGradient ? 300 : 400
              }}
            >
              {project.description || 'Нет описания'}
            </Typography>
          </CardContent>

          <Divider />

          <CardFooter>
            <Chip
              size="small"
              label={getStatusLabel(project.status)}
              color={getStatusColor(project.status)}
              sx={{
                height: 24,
                fontSize: '0.75rem',
                '& .MuiChip-label': { px: 1.2 },
                ...(hasGradient && {
                  backgroundColor: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' }
                })
              }}
            />
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                color: hasGradient ? 'rgba(255,255,255,0.7)' : 'text.secondary'
              }}
            >
              {project.member_count || 1} {project.member_count === 1 ? 'участник' : 'участников'}
            </Typography>
          </CardFooter>
        </ContentContainer>
      </CardLayer>
    </CardStack>
  );
};

export default ProfileProjectCard;